/**
 * NewPermanentModal — 2-step wizard for onboarding an indépendant.
 *
 * Step 1 · Identity  (civilité, nom, prénom, adresse, SIRET, région…)
 * Step 2 · Contract  (2 workflows au choix)
 *   a. GENERATE — période + grille tarifaire → PREREC produit le PDF
 *   b. UPLOAD   — période + fichier PDF déjà signé
 *
 * Une fois créé, le modal se ferme et la liste se recharge.
 */
import { useRef, useState } from "react";
import { FileScan, FileUp, Loader2, ScanLine, Sparkles, Upload, X } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/api";


const REGIONS = [
    { value: "idf", label: "Île-de-France" },
    { value: "grand_est", label: "Grand Est" },
    { value: "alsace", label: "Alsace" },
    { value: "nouvelle_aquitaine", label: "Nouvelle-Aquitaine" },
    { value: "occitanie", label: "Occitanie" },
    { value: "paca", label: "PACA" },
];


export default function NewPermanentModal({ onClose, onCreated }) {
    const [step, setStep] = useState(1);
    const [saving, setSaving] = useState(false);
    // Step 1
    const [id1, setId1] = useState({
        civilite: "Monsieur",
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        address_line1: "",
        address_postal_code: "",
        address_city: "",
        birth_date: "",
        legal_status: "autoentrepreneur",
        siret: "",
        region: "idf",
    });
    // Step 2
    const [permanentId, setPermanentId] = useState(null); // set after step-1 save
    const [mode, setMode] = useState("generate");   // 'generate' | 'upload'
    const [contract, setContract] = useState({
        start_date: "",
        end_date: "",
        duration_kind: "month",
        monthly_minimum_bulletins: 30,
        primary_region: "Île-De-France",
    });
    const [signedFile, setSignedFile] = useState(null);
    // OCR — contract auto-scan (iter68)
    const scanInputRef = useRef(null);
    const [scanning, setScanning] = useState(false);
    const [scanFilled, setScanFilled] = useState(false);      // one-shot banner state
    const [scanFilledFields, setScanFilledFields] = useState([]);
    const [scanCodeRdHit, setScanCodeRdHit] = useState(null); // {code, statut, ...}

    const validId1 = id1.first_name && id1.last_name && id1.email && id1.address_line1
        && id1.address_postal_code && id1.address_city && /^\d{14}$/.test(id1.siret);
    const validContract = contract.start_date && contract.end_date
        && (mode === "generate" || (mode === "upload" && signedFile));

    async function submitIdentity() {
        if (!validId1) {
            toast.error("Champs identité incomplets (SIRET 14 chiffres obligatoire)");
            return;
        }
        setSaving(true);
        try {
            const payload = { ...id1 };
            if (scanCodeRdHit?.code) payload.code_rd = scanCodeRdHit.code;
            const { data } = await api.post("/permanents", payload);
            setPermanentId(data.id);
            toast.success("Prestataire créé — passe à l'étape contrat");
            setStep(2);
        } catch (e) {
            const msg = e?.response?.data?.detail || e.message;
            toast.error("Création impossible : " + msg);
        } finally {
            setSaving(false);
        }
    }

    /**
     * Contract OCR — upload an existing "Contrat de Prestation de
     * Services" PDF/image; Claude Sonnet Vision extracts every field
     * needed by step 1 (identity) AND pre-fills step 2 (dates). Also
     * attaches a code_rd hit if the prestataire's name matches an
     * entry in the historical registre.
     */
    async function onScanContract(file) {
        if (!file) return;
        setScanning(true);
        setScanFilled(false);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/permanents/scan-contract", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 60000,
            });
            const ex = data?.extracted || {};
            // Compute `filled` + `next` SYNCHRONOUSLY here — never inside a
            // setState updater callback (React 18 defers those, so
            // `filled.length` would read 0 before the updater runs).
            const filled = [];
            const next = { ...id1 };
            const setIf = (k, v) => {
                if (v && !id1[k]) { next[k] = v; filled.push(k); }
            };
            setIf("civilite", ex.civilite);
            setIf("first_name", ex.first_name);
            setIf("last_name", ex.last_name);
            setIf("email", ex.email);
            setIf("phone", ex.phone);
            setIf("address_line1", ex.address_line1);
            setIf("address_postal_code", ex.address_postal_code);
            setIf("address_city", ex.address_city);
            setIf("birth_date", ex.birth_date);
            setIf("legal_status", ex.legal_status);
            if (ex.siret && !id1.siret) { next.siret = ex.siret; filled.push("siret"); }
            // Region: try to map primary_region to short code
            if (ex.primary_region && (id1.region === "idf" || !id1.region)) {
                const norm = String(ex.primary_region).toLowerCase();
                if (norm.includes("occitanie")) next.region = "occitanie";
                else if (norm.includes("aquitaine")) next.region = "nouvelle_aquitaine";
                else if (norm.includes("paca") || norm.includes("provence")) next.region = "paca";
                else if (norm.includes("alsace")) next.region = "alsace";
                else if (norm.includes("grand est") || norm.includes("grand-est")) next.region = "grand_est";
                else next.region = "idf";
                if (next.region !== id1.region) filled.push("region");
            }
            setId1(next);
            // Merge into contract prefill for step 2
            setContract((prev) => ({
                ...prev,
                start_date: ex.start_date || prev.start_date,
                end_date: ex.end_date || prev.end_date,
                duration_kind: ex.duration_kind || prev.duration_kind,
                primary_region: ex.primary_region || prev.primary_region,
                monthly_minimum_bulletins: ex.monthly_minimum_bulletins || prev.monthly_minimum_bulletins,
            }));
            setScanFilledFields(filled);
            setScanCodeRdHit(data?.code_rd_hit || null);
            setScanFilled(true);
            toast.success(
                `Contrat analysé — ${filled.length} champ${filled.length > 1 ? "s" : ""} pré-rempli${filled.length > 1 ? "s" : ""}`
                + (data?.code_rd_hit?.code ? ` · Code recruteur ${data.code_rd_hit.code} détecté` : "")
            );
        } catch (e) {
            const msg = e?.response?.data?.detail || e.message;
            toast.error("Analyse impossible : " + msg);
        } finally {
            setScanning(false);
            if (scanInputRef.current) scanInputRef.current.value = "";
        }
    }

    async function submitContract() {
        if (!validContract) {
            toast.error("Renseigne dates + PDF signé (si mode upload)");
            return;
        }
        setSaving(true);
        try {
            if (mode === "generate") {
                await api.post(`/permanents/${permanentId}/contracts`, {
                    ...contract,
                    origin: "generated",
                });
                toast.success("Contrat généré — télécharge-le depuis la fiche pour le faire signer");
            } else {
                // Multipart upload
                const fd = new FormData();
                fd.append("start_date", contract.start_date);
                fd.append("end_date", contract.end_date);
                fd.append("duration_kind", contract.duration_kind);
                fd.append("monthly_minimum_bulletins", String(contract.monthly_minimum_bulletins));
                fd.append("primary_region", contract.primary_region);
                fd.append("file", signedFile);
                await api.post(`/permanents/${permanentId}/contracts/upload`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                toast.success("Contrat signé importé");
            }
            try { window.dispatchEvent(new Event("permanents:changed")); } catch { /* ignore */ }
            onCreated?.();
        } catch (e) {
            const msg = e?.response?.data?.detail || e.message;
            toast.error("Contrat impossible : " + msg);
        } finally {
            setSaving(false);
        }
    }

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={onClose}
            data-testid="new-permanent-modal"
        >
            <div
                className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-slate-100">
                    <div className="flex items-start gap-3">
                        <div className="h-11 w-11 rounded-2xl bg-shariti-orange-soft text-shariti-orange flex items-center justify-center shrink-0">
                            <Sparkles className="h-5 w-5" />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-shariti-black">Nouveau permanent indépendant</h2>
                            <p className="text-[12px] text-slate-500 mt-0.5">
                                Étape {step} sur 2 · {step === 1 ? "Identité du prestataire" : "Contrat de prestation"}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} data-testid="new-permanent-close" className="h-8 w-8 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 inline-flex items-center justify-center transition">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                    {step === 1 && (
                        <div className="space-y-4" data-testid="new-permanent-step-1">
                            {/* Contract-scan shortcut — iter68 */}
                            <div
                                className={`rounded-2xl border-2 border-dashed p-4 flex items-center gap-3 transition ${
                                    scanFilled
                                        ? "border-emerald-300 bg-emerald-50"
                                        : "border-shariti-orange/40 bg-shariti-orange-soft/40 hover:bg-shariti-orange-soft/70"
                                }`}
                                data-testid="new-permanent-scan-block"
                            >
                                <div className={`h-10 w-10 rounded-2xl flex items-center justify-center shrink-0 ${
                                    scanFilled ? "bg-emerald-100 text-emerald-700" : "bg-white text-shariti-orange"
                                }`}>
                                    {scanning ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileScan className="h-5 w-5" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    {scanFilled ? (
                                        <>
                                            <div className="text-[13px] font-black text-emerald-800">
                                                Contrat analysé · {scanFilledFields.length} champ{scanFilledFields.length > 1 ? "s" : ""} pré-rempli{scanFilledFields.length > 1 ? "s" : ""}
                                            </div>
                                            <div className="text-[11px] text-emerald-700 mt-0.5 leading-snug">
                                                {scanCodeRdHit?.code
                                                    ? <>Code recruteur détecté : <b className="mono">{scanCodeRdHit.code}</b> (historique : {scanCodeRdHit.prenom_historique || ""} {scanCodeRdHit.nom_historique || ""}). Il sera associé automatiquement.</>
                                                    : "Aucun code recruteur détecté — sera pris en charge manuellement."}
                                                <br />Relis chaque champ, complète, puis Suivant →
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="text-[13px] font-black text-shariti-black">
                                                Scanner un contrat existant · gain de temps
                                            </div>
                                            <div className="text-[11px] text-slate-600 mt-0.5 leading-snug">
                                                Upload l&apos;ancien contrat PDF (ou photo) — l&apos;IA extrait nom, prénom, adresse, SIRET, dates, code recruteur…
                                            </div>
                                        </>
                                    )}
                                </div>
                                <input
                                    ref={scanInputRef}
                                    type="file"
                                    accept="application/pdf,image/*"
                                    className="hidden"
                                    data-testid="new-permanent-scan-input"
                                    onChange={(e) => onScanContract(e.target.files?.[0])}
                                    disabled={scanning}
                                />
                                <button
                                    type="button"
                                    onClick={() => scanInputRef.current?.click()}
                                    disabled={scanning}
                                    data-testid="new-permanent-scan-btn"
                                    className={`rounded-full px-4 py-2 text-[11px] font-black uppercase tracking-widest transition disabled:opacity-40 disabled:cursor-not-allowed ${
                                        scanFilled
                                            ? "bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                                            : "bg-shariti-orange text-white hover:opacity-90"
                                    }`}
                                >
                                    {scanning ? "Analyse…" : (scanFilled ? "Re-scanner" : "Scanner")}
                                </button>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Civilité *">
                                    <select
                                        value={id1.civilite}
                                        onChange={(e) => setId1({ ...id1, civilite: e.target.value })}
                                        data-testid="new-permanent-civilite"
                                        className="input-shariti"
                                    >
                                        <option value="Monsieur">Monsieur</option>
                                        <option value="Madame">Madame</option>
                                    </select>
                                </Field>
                                <Field label="Région *">
                                    <select
                                        value={id1.region}
                                        onChange={(e) => setId1({ ...id1, region: e.target.value })}
                                        className="input-shariti"
                                    >
                                        {REGIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                    </select>
                                </Field>
                                <Field label="Prénom *">
                                    <input value={id1.first_name} onChange={(e) => setId1({ ...id1, first_name: e.target.value })}
                                        data-testid="new-permanent-first-name" className="input-shariti" />
                                </Field>
                                <Field label="Nom *">
                                    <input value={id1.last_name} onChange={(e) => setId1({ ...id1, last_name: e.target.value })}
                                        data-testid="new-permanent-last-name" className="input-shariti" />
                                </Field>
                                <Field label="Email *">
                                    <input type="email" value={id1.email} onChange={(e) => setId1({ ...id1, email: e.target.value })}
                                        data-testid="new-permanent-email" className="input-shariti" />
                                </Field>
                                <Field label="Téléphone">
                                    <input value={id1.phone} onChange={(e) => setId1({ ...id1, phone: e.target.value })}
                                        className="input-shariti" placeholder="+33…" />
                                </Field>
                                <Field label="Adresse *" full>
                                    <input value={id1.address_line1} onChange={(e) => setId1({ ...id1, address_line1: e.target.value })}
                                        data-testid="new-permanent-address" className="input-shariti" placeholder="ex. 57 Rue Daguerre" />
                                </Field>
                                <Field label="Code postal *">
                                    <input value={id1.address_postal_code}
                                        onChange={(e) => setId1({ ...id1, address_postal_code: e.target.value })}
                                        className="input-shariti" placeholder="75014" />
                                </Field>
                                <Field label="Ville *">
                                    <input value={id1.address_city} onChange={(e) => setId1({ ...id1, address_city: e.target.value })}
                                        className="input-shariti" placeholder="Paris" />
                                </Field>
                                <Field label="Date de naissance">
                                    <input type="date" value={id1.birth_date} onChange={(e) => setId1({ ...id1, birth_date: e.target.value })}
                                        className="input-shariti" />
                                </Field>
                                <Field label="Statut fiscal *">
                                    <input value={id1.legal_status} onChange={(e) => setId1({ ...id1, legal_status: e.target.value })}
                                        className="input-shariti" />
                                </Field>
                                <Field label="SIRET (14 chiffres) *">
                                    <input value={id1.siret}
                                        onChange={(e) => setId1({ ...id1, siret: e.target.value.replace(/\D/g, "").slice(0, 14) })}
                                        data-testid="new-permanent-siret"
                                        className="input-shariti mono"
                                        placeholder="14 chiffres" />
                                </Field>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="space-y-4" data-testid="new-permanent-step-2">
                            {/* Mode picker */}
                            <div className="grid grid-cols-2 gap-2">
                                <ModeCard
                                    active={mode === "generate"}
                                    onClick={() => setMode("generate")}
                                    icon={ScanLine}
                                    title="Générer le contrat"
                                    subtitle="PREREC produit le PDF prêt à faire signer"
                                    testid="new-permanent-mode-generate"
                                />
                                <ModeCard
                                    active={mode === "upload"}
                                    onClick={() => setMode("upload")}
                                    icon={Upload}
                                    title="Importer un contrat signé"
                                    subtitle="Upload d'un PDF déjà signé à l'extérieur"
                                    testid="new-permanent-mode-upload"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Date de début *">
                                    <input type="date" value={contract.start_date}
                                        onChange={(e) => setContract({ ...contract, start_date: e.target.value })}
                                        data-testid="new-permanent-start-date" className="input-shariti" />
                                </Field>
                                <Field label="Date de fin *">
                                    <input type="date" value={contract.end_date}
                                        onChange={(e) => setContract({ ...contract, end_date: e.target.value })}
                                        data-testid="new-permanent-end-date" className="input-shariti" />
                                </Field>
                                <Field label="Cadence">
                                    <select value={contract.duration_kind}
                                        onChange={(e) => setContract({ ...contract, duration_kind: e.target.value })}
                                        data-testid="new-permanent-duration-kind" className="input-shariti">
                                        <option value="month">Mensuel</option>
                                        <option value="year">Annuel</option>
                                        <option value="custom">Personnalisé</option>
                                    </select>
                                </Field>
                                <Field label="Zone d'exercice">
                                    <input value={contract.primary_region}
                                        onChange={(e) => setContract({ ...contract, primary_region: e.target.value })}
                                        className="input-shariti" />
                                </Field>
                                <Field label="Bulletins nets min./mois">
                                    <input type="number" min="0" value={contract.monthly_minimum_bulletins}
                                        onChange={(e) => setContract({ ...contract, monthly_minimum_bulletins: parseInt(e.target.value || "30", 10) })}
                                        className="input-shariti" />
                                </Field>
                            </div>

                            {mode === "upload" && (
                                <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 p-4">
                                    <label className="block cursor-pointer" data-testid="new-permanent-upload-label">
                                        <input
                                            type="file"
                                            accept="application/pdf"
                                            className="hidden"
                                            data-testid="new-permanent-file-input"
                                            onChange={(e) => setSignedFile(e.target.files?.[0] || null)}
                                        />
                                        <div className="flex items-center gap-3">
                                            <FileUp className="h-5 w-5 text-shariti-orange" />
                                            <div className="flex-1">
                                                <div className="text-[13px] font-bold text-shariti-black">
                                                    {signedFile ? signedFile.name : "Cliquer pour choisir un PDF signé"}
                                                </div>
                                                <div className="text-[11px] text-slate-500">
                                                    Max 15 Mo — format PDF uniquement
                                                </div>
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            )}

                            {mode === "generate" && (
                                <div className="rounded-2xl bg-blue-50 border border-blue-200 p-3 text-[12px] text-blue-900 leading-snug">
                                    Grille tarifaire par défaut (Nefis) : 1-6 bulletins/sem → 35 €, 7-12 → 50 €,
                                    13-18 → 65 €, 19+ → 80 €. Édition possible plus tard sur la fiche.
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-slate-100 px-6 py-4 flex items-center justify-between gap-3">
                    <button
                        onClick={step === 2 ? () => setStep(1) : onClose}
                        className="rounded-full border border-slate-200 px-4 py-2 text-[12px] font-bold text-slate-600 hover:bg-slate-50"
                    >
                        {step === 2 ? "← Retour" : "Annuler"}
                    </button>
                    <button
                        onClick={step === 1 ? submitIdentity : submitContract}
                        disabled={saving || (step === 1 ? !validId1 : !validContract)}
                        data-testid={step === 1 ? "new-permanent-next-btn" : "new-permanent-submit-btn"}
                        className="rounded-full bg-shariti-orange text-white px-5 py-2 text-[12px] font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        {step === 1 ? "Suivant →" : (mode === "generate" ? "Générer le contrat" : "Importer le contrat")}
                    </button>
                </div>
            </div>
        </div>
    );
}


function Field({ label, children, full = false }) {
    return (
        <div className={full ? "col-span-2" : ""}>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                {label}
            </label>
            {children}
        </div>
    );
}


function ModeCard({ active, onClick, icon: Icon, title, subtitle, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`text-left rounded-2xl border-2 px-4 py-3 transition ${
                active
                    ? "border-shariti-orange bg-shariti-orange-soft"
                    : "border-slate-200 hover:border-slate-300 bg-white"
            }`}
        >
            <Icon className={`h-5 w-5 mb-1.5 ${active ? "text-shariti-orange" : "text-slate-400"}`} />
            <div className="text-[13px] font-black text-shariti-black">{title}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">{subtitle}</div>
        </button>
    );
}
